/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useState } from 'react';
import './TwSendOrderDocs.scss';
import { Formik, Form } from 'formik';
import LoadsService from '../../providers/Loads/Loads.service';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwCheckbox from '../TwCheckboxes/TwCheckboxes';
import TwTooltip from '../TwTooltip/TwTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Divider, Stack } from '@mui/material';
import { Warning } from '@mui/icons-material';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import TwTagsField, { TwTagsOption } from '../TwTagsField/TwTagsField';
import TwShareHistory from '../TwLoadShareHistory/TwLoadShareHistory';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useAuth from '../../providers/Auth/Auth.provider';
import { getCustomerContactEmails } from '../../utils/contactUtils';
import { ShareType } from '../../redux/api/load/load.model';
import TwSelectableDocsPhotos from '../TwSelectableDocsPhotos/TwSelectableDocsPhotos.';
import FileUtils from '../../utils/fileUtils';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useLocalStorageState from 'use-local-storage-state';
import { ClientResponse } from '../../redux/api/customer/customer.model';
import {
  CLIENT_TYPE,
  Order,
  OrderAttachment,
  SendOrderInfoByEmailParams
} from '../../redux/api/orders/orders.model';
import {
  useGetOrderByIdQuery,
  useSendOrderInfoByEmailMutation
} from '../../redux/api/orders/ordersApi';
import TwOrderTripsDocViewer from '../TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';

const MAX_ATTACHMENT_SIZE_MB = 28;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export interface TwSendOrderDocsProps {
  /**
   * When true dialog is open
   */
  open: boolean;
  /**
   * Order id
   */
  orderId: string;
  /**
   * Action to perform when dialog closes
   */
  onClose: () => void;
  /**
   * Action to perform when submit is successful
   */
  onSuccess: () => void;
}

interface TwSendOrderDocsFormValues {
  includeSummary: boolean;
  includeInvoice: boolean;
}

const TwSendOrderDocs: React.FC<TwSendOrderDocsProps> = ({
  open,
  orderId,
  onClose,
  onSuccess
}): JSX.Element => {
  const [client, setClient] = useState<ClientResponse | null | undefined>();
  const [photosToDisplay, setPhotosToDisplay] = useState<OrderAttachment[]>([]);
  const [documents, setDocuments] = useState<OrderAttachment[]>([]);

  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [attachmentSizeWarningOpen, setAttachmentSizeWarningOpen] = useState<boolean>(false);
  const [tenantCc, setTenantCc] = useState<boolean>(true);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [invoice, setInvoice] = useState<boolean>(true);
  const [emailsToSend, setEmailsToSend] = useState<string[] | undefined>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [hasHistory, setHasHistory] = useState(false);
  const { addNotification } = useNotifications();
  const [suggestedEmails, setSuggestedEmails] = useState<TwTagsOption[]>([]);
  const [filesToView, setFilesToView] = useState<OrderAttachment[]>([]);
  const [docsViewerOpen, setDocsViewerOpen] = useState(false);

  const [mergeAttachments, setMergeAttachments] = useLocalStorageState('mergeAttachments', {
    defaultValue: false
  });

  const [selectedFileToView, setSelectedFileToView] = useState<OrderAttachment>(
    {} as OrderAttachment
  );
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();

  const includeNoticeOfAssignmentEnabled = getTenantSetting(
    'enable-factoring-notice-of-assignment'
  );
  const useCustomerOrderNumber = getTenantSetting('customer-load-nbr-as-primary');

  const [sendOrderInfoByEmail] = useSendOrderInfoByEmailMutation();

  const initialValues: TwSendOrderDocsFormValues = {
    includeSummary: true,
    includeInvoice: true
  };

  const { data: order } = useGetOrderByIdQuery(
    { orderId },
    {
      skip: !orderId || !open
    }
  );

  const getLocationContactEmailTags = (order: Order): TwTagsOption[] => {
    const tags: any = [];

    order?.trips?.map((segment) => {
      segment?.stops?.map((stop) => {
        if (stop?.contact?.contact_email) {
          tags.push({
            id: '',
            label: stop?.contact?.contact_name || stop?.contact?.contact_email,
            subLabel: stop?.contact?.contact_email,
            value: stop?.contact?.contact_email
          });
        }
      });
    });
    return tags ? tags : [];
  };

  useEffect(() => {
    if (!orderId) return;

    setInvoice(!!order?.invoice ? true : false);

    const client = order?.primary_client === CLIENT_TYPE.SHIPPER ? order?.shipper : order?.broker;

    const locationContacts = order ? getLocationContactEmailTags(order) : [];

    order?.raw_customer &&
      setSuggestedEmails([
        ...getCustomerContactEmails(
          order.raw_customer,
          includeNoticeOfAssignmentEnabled,
          userData?.factoringCompany
        ),
        ...locationContacts
      ]);
    setClient(client);
    setEmailMessage(
      `Hello ${client?.name}, \nIn this email you can find order #${
        useCustomerOrderNumber ? order?.customer_order_number || '' : order?.readable_id || ''
      } basic info. To see current status and more details click on the button below.`
    );
    setEmailSubject(
      `Order Ref #${
        useCustomerOrderNumber ? order?.customer_order_number || '' : order?.readable_id || ''
      } from ${order?.division?.name}`
    );
    setEmailsToSend(client?.contact?.email_address ? [client?.contact?.email_address] : []);
  }, [order]);

  const getHistory = useCallback(async () => {
    try {
      if (!orderId) return;
      const history = await LoadsService.getShareHistory(orderId, ShareType.load_update);
      setHasHistory(history.length > 0 ? true : false);
    } catch {
      console.error('Error getting share history');
    }
  }, [orderId]);

  const sendorderDocs = async (): Promise<void> => {
    setDisableSubmit(true);

    let attachmentsSize = 0;

    order?.order_attachments &&
      order?.order_attachments.map((doc) => {
        if (selectedDocs.includes(doc.id) || selectedPhotos.includes(doc.id)) {
          attachmentsSize = attachmentsSize + doc.attachment.size;
        }
      });

    if (attachmentsSize >= MAX_ATTACHMENT_SIZE_BYTES) {
      setAttachmentSizeWarningOpen(true);
      return;
    }

    const params: SendOrderInfoByEmailParams = {
      attachments: [...selectedDocs, ...selectedPhotos],
      tenant_cc: tenantCc,
      send_to: emailsToSend ?? [],
      include_invoice: invoice,
      email_message: emailMessage,
      subject: emailSubject,
      merge_attachments: mergeAttachments
    };

    try {
      const response = await sendOrderInfoByEmail({ orderId: orderId, params }).unwrap();

      if (response) {
        onSuccess();
        setDisableSubmit(false);
        setEmailsToSend([]);
        setSelectedDocs([]);
        setSelectedPhotos([]);

        addNotification({
          message: `Order ${order && order?.readable_id} has been sent`,
          variant: 'success'
        });
      }
    } catch (e) {
      logRocketTrack('order Management', {
        action: 'Email load info',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error sending update',
        variant: 'error'
      });
      setDisableSubmit(false);
    } finally {
      setCurrentStep(0);
    }
  };

  const handleClose = (): void => {
    setEmailsToSend([]);
    setSelectedDocs([]);
    setSelectedPhotos([]);
    setCurrentStep(0);
    onClose();
  };

  useEffect(() => {
    if (open && order) {
      const photosFromDocs = order.order_attachments.filter((docs) => {
        return docs.attachment.file_type.includes('image');
      });
      const onlyDocs = order.order_attachments.filter((docs) => {
        return !docs.attachment.file_type.includes('image');
      });

      if (photosFromDocs) {
        (async (): Promise<void> => {
          try {
            const newPhotosToDisplay = await FileUtils.getPhotosInBatch(photosFromDocs ?? [], 5);

            setPhotosToDisplay(newPhotosToDisplay);
          } catch (e) {
            console.error(e);
          }
        })();
      }
      if (onlyDocs) {
        setDocuments(onlyDocs);
      }
    }
  }, [open, order]);

  useEffect(() => {
    open &&
      (async (): Promise<void> => {
        await getHistory();
      })();
  }, [open]);

  useEffect(() => {
    setDisableSubmit(emailsToSend?.length === 0);
  }, [emailsToSend]);

  const handleSelectedEmails = (emails: string[]): void => {
    if (emails.length !== 0) {
      emails.map((email) => {
        if (emailsToSend?.indexOf(email) === -1) {
          emailsToSend && setEmailsToSend((prev) => prev && [...prev, email]);
        }
      });
    }
    setEmailsToSend(emails);
  };

  const handleBack = (): void => {
    setCurrentStep((currentStep) => currentStep - 1);
  };

  const handleNext = (): void => {
    // If no email present, donot proceed to next step
    if (emailsToSend && emailsToSend?.length > 0) {
      setCurrentStep((currentStep) => currentStep + 1);
    } else {
      logRocketTrack('order Management', {
        action: 'Email order info',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please add email',
        variant: 'error'
      });
    }
  };

  const handleCloseErrorWarning = (): void => {
    setAttachmentSizeWarningOpen(false);
    setDisableSubmit(false);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={(): void => {
          sendorderDocs();
        }}
      >
        {({ handleSubmit }): JSX.Element => (
          <Form>
            <TwDialog
              open={open}
              onClickClose={handleClose}
              size="sm"
              title="Email order info"
              actions={[
                <TwButton
                  key="1"
                  fullwidth={false}
                  type={TwButtonType.secondary}
                  label={`${currentStep === 0 ? 'Cancel' : 'Back'}`}
                  action={(): void => {
                    currentStep === 0 ? handleClose() : handleBack();
                  }}
                />,
                <TwButton
                  key="2"
                  fullwidth={false}
                  type={TwButtonType.primary}
                  disabled={disableSubmit}
                  label={`${
                    currentStep === 1 || (currentStep === 0 && !hasHistory) ? 'Send' : 'Next'
                  }`}
                  action={(): void => {
                    currentStep === 1 || (currentStep === 0 && !hasHistory)
                      ? handleSubmit()
                      : handleNext();
                  }}
                />
              ]}
              fullWidthAction
            >
              <div className="tid-send-order-docs__wrapper">
                <div className="tid-send-order-docs__send-to send-order-docs__send-to">
                  <TwTagsField
                    id="emailListTB"
                    label="Send to:"
                    tagsDisplayed={
                      emailsToSend || (client?.billing_email ? [client?.billing_email] : [])
                    }
                    tagType={'email'}
                    suggestedOptions={suggestedEmails}
                    onTagAdded={(emails): void => handleSelectedEmails(emails)}
                  />
                </div>
                {currentStep === 0 && hasHistory ? (
                  <TwShareHistory id={orderId} shareType={ShareType.load_update} />
                ) : (
                  <>
                    <div>
                      <div className="tid-send-order-docs__subtitle send-order-docs__subtitle">
                        Subject
                      </div>
                      <TwField
                        id="emailSubject"
                        name="emailSubject"
                        inputType={TwInputTypes.text}
                        value={emailSubject}
                        onInputChange={(e): void => {
                          setEmailSubject(e.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <div className="tid-send-order-docs__subtitle send-order-docs__subtitle">
                        Message
                      </div>
                      <TwField
                        id="emailMessage"
                        name="emailMessage"
                        inputType={TwInputTypes.textarea}
                        value={emailMessage}
                        onInputChange={(e): void => {
                          setEmailMessage(e.target.value);
                        }}
                      />
                    </div>

                    <Divider sx={{ margin: '12px 0' }} />

                    {order && order.invoice && (
                      <div className="tid-send-order-docs__order-invoice">
                        <TwCheckbox
                          name="includeInvoice"
                          label="Include invoice"
                          defaultChecked={initialValues.includeInvoice}
                          onInputChange={(): void => {
                            setInvoice((invoice) => !invoice);
                          }}
                        />
                        <TwTooltip title="This is the invoice of the order" placement="right" arrow>
                          <InfoOutlinedIcon sx={{ height: '16px', color: '#7e858e' }} />
                        </TwTooltip>
                      </div>
                    )}

                    <TwSelectableDocsPhotos
                      documents={documents}
                      photos={photosToDisplay}
                      onSelectDocuments={setSelectedDocs}
                      onSelectPhotos={setSelectedPhotos}
                      onDocumentClick={(document): void => {
                        setFilesToView(documents);
                        setSelectedFileToView(document);
                        setDocsViewerOpen(true);
                      }}
                      onPhotoClick={(photo): void => {
                        setFilesToView(photosToDisplay);
                        setSelectedFileToView(photo);
                        setDocsViewerOpen(true);
                      }}
                      disableEncryptedDocuments={mergeAttachments}
                    />

                    <Stack
                      direction="column"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <TwCheckbox
                        label="Merge attachments"
                        name="mergeAttachments"
                        onInputChange={(): void => {
                          setMergeAttachments((prev) => !prev);
                        }}
                        checked={mergeAttachments}
                      />

                      <TwCheckbox
                        label="Send me a copy"
                        name="tenantCc"
                        onInputChange={(): void => {
                          setTenantCc((prev) => !prev);
                        }}
                        checked={tenantCc}
                      />
                    </Stack>
                  </>
                )}
              </div>
            </TwDialog>
          </Form>
        )}
      </Formik>
      <TwDialog
        open={attachmentSizeWarningOpen}
        title="Unable to send order docs"
        onClickClose={handleCloseErrorWarning}
      >
        <div className="warning">
          <Warning />
          <span className="warning__message">
            Selected attachments exceed {MAX_ATTACHMENT_SIZE_MB} MB. Please remove attachments or
            decrease file sizes to send order docs.
          </span>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label="Okay"
              type={TwButtonType.primary}
              action={handleCloseErrorWarning}
            />
          </div>
        </div>
      </TwDialog>

      <TwOrderTripsDocViewer
        readableId={order?.readable_id}
        files={filesToView}
        open={docsViewerOpen}
        onClickClose={(): void => setDocsViewerOpen(false)}
        selectedFileId={selectedFileToView.id}
      />
    </>
  );
};

export default TwSendOrderDocs;
